import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useTheme } from '../../theme-context';
import Button from '../button';
import { convertMarkdownToHTML } from '../../utils/convertMarkdownToHTML';
import Loading from '../Loading';
import { DEVICE_INFO_API_URL } from '../../config';

interface IAiMessages {
    text: string;
    files?: { name: string; url: string }[] | null;
    isNew?: boolean;
    data?: any;
    showActions?: boolean;
    considerDarkMode?: boolean;
    isTyping: boolean;
    setIsTyping: (val: boolean) => void;
    onClickAction?: (action: string, id: string) => void;
    onClickLinks?: (type: string) => void;
    maxLineLength?: number;
    isAuth?: boolean;
	audioRef?: MutableRefObject<HTMLAudioElement | { pause: () => void; paused: boolean; currentTime: number } | null>
}

const AiMessages = ({
    text,
    files,
    isNew = false,
    isTyping,
    setIsTyping,
    data,
    showActions,
    considerDarkMode,
    onClickAction,
    onClickLinks,
    maxLineLength = 100,
    isAuth,
	audioRef
}: IAiMessages) => {
    const { theme } = useTheme();
    const [previews, setPreviews] = useState<
        { name: string; url: string; type: string; preview?: any[][] }[]
    >([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const objectUrlRef = useRef<string | null>(null);
    const [isVoiceLoading, setIsVoiceLoading] = useState<boolean>();
    const [arrayHtml, setArrayHtml] = useState<string[][]>();
    const [isSpeaking, setIsSpeaking] = useState(false);

    const boxLength = Number(
        document.querySelector('.scrollable-div')?.clientWidth,
    );

    maxLineLength = isAuth
        ? maxLineLength
        : boxLength > 900
        ? maxLineLength
        : boxLength > 720
        ? 80
        : boxLength > 400
        ? 50
        : 30;

    function splitTextPreservingHtml(text: string): string[] {
        const cleanedText = text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();

        const lines: string[] = [];
        let currentLine = '';
        let inTag = false;
        let tagBuffer = '';
        let visible = 0; // counts only visible chars (outside tags)
        let anchorDepth = 0; // >0 means inside <a>…</a>
        let lastSafeHtmlLen: number | null = null; // string length of currentLine at last safe split
        let lastSafeVisible: number | null = null; // visible count at last safe split

        const pushLine = (sliceLen?: number, sliceVisible?: number) => {
            if (sliceLen != null) {
                // push prefix
                const head = currentLine.slice(0, sliceLen);
                if (head.trim()) lines.push(head.trim());
                // keep remainder in buffer
                currentLine = currentLine.slice(sliceLen);
                visible = Math.max(0, visible - (sliceVisible ?? 0));
            } else {
                const s = currentLine.trim();
                if (s) lines.push(s);
                currentLine = '';
                visible = 0;
            }
            lastSafeHtmlLen = null;
            lastSafeVisible = null;
        };

        for (let i = 0; i < cleanedText.length; i++) {
            const ch = cleanedText[i];

            // tag start
            if (ch === '<') {
                inTag = true;
                tagBuffer = '<';
                continue;
            }

            // tag end
            if (ch === '>' && inTag) {
                tagBuffer += '>';
                inTag = false;

                const lower = tagBuffer.toLowerCase();

                // <li> or <p> starts a new array element
                if (/^<(li|p)[\s>]/.test(lower)) {
                    if (currentLine.trim()) pushLine(); // flush whatever was before
                    currentLine += tagBuffer; // start chunk with the opening tag
                    tagBuffer = '';
                    continue;
                }

                // </li> closes this element; include it and flush
                if (lower.startsWith('</li')) {
                    currentLine += tagBuffer;
                    pushLine(); // flush li chunk
                    tagBuffer = '';
                    continue;
                }

                // track anchors so we don't split inside links
                if (/^<a[\s>]/.test(lower)) {
                    anchorDepth++;
                } else if (lower.startsWith('</a')) {
                    anchorDepth = Math.max(0, anchorDepth - 1);
                }

                currentLine += tagBuffer; // append any other tag
                tagBuffer = '';
                continue;
            }

            // inside tag
            if (inTag) {
                tagBuffer += ch;
                continue;
            }

            // newline boundary
            if (ch === '\n') {
                if (currentLine.trim()) pushLine();
                continue;
            }

            // visible text
            currentLine += ch;
            visible += 1;

            // update "last safe split" marker on spaces OUTSIDE anchors
            if (ch === ' ' && anchorDepth === 0) {
                lastSafeHtmlLen = currentLine.length; // HTML length up to and including this space
                lastSafeVisible = visible; // visible count up to this space
            }

            // if we've exceeded the limit, split at the LAST safe space BEFORE the limit
            if (visible > maxLineLength && lastSafeHtmlLen != null) {
                // push prefix up to last safe space
                pushLine(lastSafeHtmlLen, lastSafeVisible ?? 0);
                // NOTE: we do NOT add a delay here; the loop continues,
                // so remainder stays in currentLine and visible recalculated correctly
            }
        }

        // flush tail
        if (currentLine.trim()) pushLine();

        return lines;
    }

    useEffect(() => {
        const chunks = text ? convertMarkdownToHTML(text) : '';
        let fullOutputArray: string[][] = [];
        let charIndex = 0;

        const typeNext = () => {
            const lines = chunks
                .split(/\n+/)
                .filter((chunk) => chunk.trim() !== '')
                .map((chunk) => splitTextPreservingHtml(chunk));

            if (charIndex < lines.length) {
                fullOutputArray = [...fullOutputArray, lines[charIndex]];
                setArrayHtml(fullOutputArray);
                charIndex++;
                setTimeout(typeNext, 3000);
            }
        };

        typeNext();
    }, [text]);

	const cancelStreaming = () => {
		// On unmount, stop audio
		if (audioRef && audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}

		// Abort the fetch stream
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Revoke any object URLs if needed
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
	}

	useEffect(() => {
		return () => {
			cancelStreaming();
		};
	}, []);

    useEffect(() => {
        if (!files || files.length === 0) return;

        const loadPreviews = async () => {
            const loaded = await Promise.all(
                files.map(async (file) => {
                    const fileName = file?.url?.split('/').pop() || '';
                    const fileNameLower = fileName.toLowerCase();

                    const isCsv = fileNameLower.endsWith('.csv');
                    const isExcel =
                        fileNameLower.endsWith('.xls') ||
                        fileNameLower.endsWith('.xlsx');
                    const isPdf = fileNameLower.endsWith('.pdf');
                    let preview: any[][] | undefined;

                    try {
                        const res = await fetch(file.url);

                        if (isCsv) {
                            const text = await res.text();
                            preview = Papa.parse(text, { preview: 5 })
                                .data as any[][];
                        } else if (isExcel) {
                            const buffer = await res.arrayBuffer();
                            const wb = XLSX.read(buffer, { type: 'array' });
                            const sheet = wb.Sheets[wb.SheetNames[0]];
                            preview = XLSX.utils.sheet_to_json(sheet, {
                                header: 1,
                            }) as any[][];
                        }
                    } catch (err) {
                        console.warn('Preview fetch failed:', err);
                    }

                    return {
                        name: file.name,
                        url: file.url,
                        type: isCsv
                            ? 'csv'
                            : isExcel
                            ? 'excel'
                            : isPdf
                            ? 'pdf'
                            : 'other',
                        preview,
                    };
                }),
            );

            setPreviews(loaded);
        };

        loadPreviews();
    }, [files]);

    const handleVoicePlay = async () => {
        if (isVoiceLoading) return;


        try {
			cancelStreaming()
            setIsVoiceLoading(true);

            const controller = new AbortController();
            abortControllerRef.current = controller;

            const response = await fetch(
                `${DEVICE_INFO_API_URL}/tts/stream?text=${encodeURIComponent(
                    text,
                )}&voice=alloy`,
                { signal: controller.signal },
            );

            if (!response.body) {
                throw new Error('No stream body in response');
            }

            const mimeCodec = 'audio/mpeg';
            if (!MediaSource.isTypeSupported(mimeCodec)) {
                throw new Error('Codec not supported');
            }

            const mediaSource = new MediaSource();
            const audio = new Audio();
            const objectUrl = URL.createObjectURL(mediaSource);
            objectUrlRef.current = objectUrl;
            audio.src = objectUrl;
			if (audioRef)
            	audioRef.current = audio;

            mediaSource.addEventListener('sourceopen', async () => {
                const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
                const reader = response.body!.getReader();

                const readChunk = async () => {
                    try {
                        const { done, value } = await reader.read();
                        if (done) {
                            mediaSource.endOfStream();
                            return;
                        }
                        sourceBuffer.appendBuffer(value);
                    } catch (e) {
                        console.warn('Read aborted or failed:', e);
                    }
                };

                sourceBuffer.addEventListener('updateend', readChunk);
                await readChunk();
            });

            audio.play();
            setIsSpeaking(true);

            audio.addEventListener('ended', () => {
                setIsSpeaking(false);
            });
        } catch (e) {
            const error = e as { name: string };
            if (error.name === 'AbortError') {
                console.log('Streaming aborted');
            } else {
                console.error('Streaming error:', e);
            }
        } finally {
            setIsVoiceLoading(false);
        }
    };

    return (
        <div
            className={`box-message !rounded-xl m-2 py-2 px-3 bg-white ${
                considerDarkMode ? 'dark:bg-[#3a3a3a]' : ''
            } max-w-[85%]`}>
            <p
                className={`mb-2 whitespace-pre-wrap ${
                    considerDarkMode ? 'dark:text-primary-dark' : ''
                }`}>
                {/* {isTyping ? displayedText : text} */}
                {arrayHtml?.map((arr) =>
                    arr.map((item, index) => (
                        <p
                            style={{ animationDelay: `${index * 200}ms` }}
                            key={item + index}
                            className="fade-word"
                            dangerouslySetInnerHTML={{ __html: item }}
                        />
                    )),
                )}
                {/* {isTyping && <span className="animate-pulse">|</span>} */}
            </p>

            {data?.details && !isTyping && (
                <Button
                    label={data.details.title}
                    className={`btn-primary-text-underline ${
                        considerDarkMode ? 'dark:text-white' : ''
                    }`}
                    onClickButton={() => onClickLinks?.(data.details.type)}
                />
            )}

            {!isTyping && previews.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {previews.map((file, idx) => {
                        const isImage = file.name.match(
                            /\.(jpg|jpeg|png|gif|webp)$/i,
                        );

                        return (
                            <a
                                key={idx}
                                href={file.url}
                                download={file.name}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`relative border border-gray-300 rounded p-2 w-fit max-w-[300px] bg-gray-100 !text-white ${
                                    considerDarkMode
                                        ? 'dark:bg-gray-400 dark:text-black'
                                        : ''
                                } hover:shadow-lg transition-all`}>
                                {isImage ? (
                                    <img
                                        src={file.url}
                                        alt={file.name}
                                        className="w-24 h-24 object-cover rounded"
                                    />
                                ) : file.type === 'pdf' ? (
                                    <iframe
                                        src={file.url}
                                        title={file.name}
                                        className="w-48 h-32 border rounded"
                                    />
                                ) : file.preview ? (
                                    <div className="overflow-x-auto max-h-32 mb-2 border">
                                        <table className="text-xs text-left border-collapse w-full">
                                            <tbody>
                                                {file.preview.map(
                                                    (row, rowIdx) => (
                                                        <tr key={rowIdx}>
                                                            {row.map(
                                                                (
                                                                    cell,
                                                                    colIdx,
                                                                ) => (
                                                                    <td
                                                                        key={
                                                                            colIdx
                                                                        }
                                                                        className="border px-1 py-0.5 whitespace-nowrap text-black">
                                                                        {cell}
                                                                    </td>
                                                                ),
                                                            )}
                                                        </tr>
                                                    ),
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                                <div
                                    className={`bg-[#252B37f0] text-white ${
                                        considerDarkMode
                                            ? 'dark:bg-gray-200 dark:text-black'
                                            : ''
                                    } text-center text-xs rounded-lg p-2 mt-1 truncate max-w-[250px]`}>
                                    {file.name}
                                </div>
                            </a>
                        );
                    })}
                </div>
            )}

            {text && !isTyping && showActions && (
                <div className="mt-4 flex flex-row items-center">
                    <div
                        onClick={() => navigator?.clipboard?.writeText(text)}
                        className="hover:opacity-80 cursor-pointer">
                        <img
                            src={
                                theme === 'dark'
                                    ? '/assets/chat/copy-icon-white.svg'
                                    : '/assets/chat/copy-icon.svg'
                            }
                            className="object-contain h-[20px] mr-2"
                        />
                    </div>

                    {data?._id && (
                        <>
                            <div
                                className="hover:opacity-80 cursor-pointer"
                                onClick={() =>
                                    onClickAction?.('like', data._id)
                                }>
                                <img
                                    src={
                                        data?.liked
                                            ? theme === 'dark'
                                                ? '/assets/chat/like-selected-icon-white.svg'
                                                : '/assets/chat/like-selected-icon.svg'
                                            : theme === 'dark'
                                            ? '/assets/chat/like-icon-white.svg'
                                            : '/assets/chat/like-icon.svg'
                                    }
                                    className="object-contain h-[20px] mr-2"
                                />
                            </div>
                            <div
                                className="hover:opacity-80 cursor-pointer"
                                onClick={() =>
                                    onClickAction?.('dislike', data._id)
                                }>
                                <img
                                    src={
                                        data?.disliked
                                            ? theme === 'dark'
                                                ? '/assets/chat/dislike-selected-icon-white.svg'
                                                : '/assets/chat/dislike-selected-icon.svg'
                                            : theme === 'dark'
                                            ? '/assets/chat/dislike-icon-white.svg'
                                            : '/assets/chat/dislike-icon.svg'
                                    }
                                    className="object-contain h-[20px] mr-2"
                                />
                            </div>
                            <div
                                className="hover:opacity-80 cursor-pointer"
                                onClick={() =>
                                    onClickAction?.('favorite', data._id)
                                }>
                                <img
                                    src={
                                        data?.favorite
                                            ? theme === 'dark'
                                                ? '/assets/chat/favorite-selected-icon-white.svg'
                                                : '/assets/chat/favorite-selected-icon.svg'
                                            : theme === 'dark'
                                            ? '/assets/chat/favorite-icon-white.svg'
                                            : '/assets/chat/favorite-icon.svg'
                                    }
                                    className="object-contain h-[22px] mr-2"
                                />
                            </div>
                            <div
                                className="hover:opacity-80 cursor-pointer"
                                onClick={() =>
                                    onClickAction?.('report', data._id)
                                }>
                                <img
                                    src={
                                        theme === 'dark'
                                            ? '/assets/chat/report-icon-white.svg'
                                            : '/assets/chat/report-icon.svg'
                                    }
                                    className="object-contain h-[18px] mr-2"
                                />
                            </div>
                        </>
                    )}
                    <div
                        className="hover:opacity-80 cursor-pointer relative"
                        style={{ opacity: isVoiceLoading ? '0.5' : '1' }}
                        onClick={handleVoicePlay}>
                        {isVoiceLoading ? (
                            <Loading />
                        ) : (
                            <img
                                src={
                                    isSpeaking
                                        ? theme === 'dark' && considerDarkMode
                                            ? '/assets/chat/audio-selected-icon-white.svg'
                                            : '/assets/chat/audio-selected-icon.svg'
                                        : theme === 'dark' && considerDarkMode
                                        ? '/assets/chat/audio-icon-white.svg'
                                        : '/assets/chat/audio-icon.svg'
                                }
                                className={`object-contain h-[20px] mr-2 ${
                                    isSpeaking ? 'animate-pulse' : ''
                                }`}
                            />
                        )}
                        {isSpeaking && (
                            <div className="absolute top-[-4px] right-[-4px] bg-[#252B37] dark:bg-gray-300 rounded-full w-2.5 h-2.5 animate-ping"></div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AiMessages;
